import type { AppIconInput } from "./appIcon";

export type IconSource = "appstore" | "local" | "brandfetch" | "dot";

export type BrandfetchLogoIdentifier = {
  type: "domain" | "brandId";
  value: string;
};

/**
 * Explicit bundle-ID → Brandfetch domain map.
 * Used when local SVG is missing or fails to load (e.g. svgicons not on the Worker).
 */
const IDENTIFIER_BRANDFETCH_DOMAINS: Record<string, string> = {
  "com.microsoft.skype.teams": "microsoft.com",
  "com.openai.chat": "openai.com",
  "com.openai.chatgpt": "openai.com",
  "com.spotify.client": "spotify.com",
  "com.google.ios.youtube": "youtube.com",
  "com.google.android.youtube": "youtube.com",
  "com.apple.mobilesafari": "apple.com",
  "com.apple.mobilemail": "apple.com",
  "com.google.maps": "google.com",
  "com.google.android.apps.maps": "google.com",
  "net.whatsapp.whatsapp": "whatsapp.com",
  "com.google.Gmail": "google.com",
  "com.google.android.gm": "google.com",
  "com.instagram.android": "instagram.com",
  "com.instagram.ios": "instagram.com",
  "com.burbn.instagram": "instagram.com",
  "com.facebook.facebook": "facebook.com",
  "com.netflix.mediaclient": "netflix.com",
  "com.discord": "discord.com",
  "com.hammerandchisel.discord": "discord.com",
  "org.telegram.messenger": "telegram.org",
  "com.reddit.frontpage": "reddit.com",
  "com.reddit.Reddit": "reddit.com",
  "com.ubercab.UberClient": "uber.com",
  "us.zoom.videomeetings": "zoom.us",
  "com.booking.bookingapp": "booking.com",
  "net.skyscanner.iphone": "skyscanner.com",
  "com.apple.preferences": "apple.com",
  "com.withopal.opal": "withopal.com",
  "wtf.riedel.one-sec": "one-sec.app",
  "to.freedom.freedomblocker": "freedom.to",
  "com.amazon.aiv.aivapp": "primevideo.com",
  "com.cbs.canada.app": "paramountplus.com",
};

/** Friendly display labels → Brandfetch domain (fallback when bundle ID is absent). */
const DISPLAY_NAME_BRANDFETCH_DOMAINS: Record<string, string> = {
  safari: "apple.com",
  mail: "apple.com",
  youtube: "youtube.com",
  whatsapp: "whatsapp.com",
  "google maps": "google.com",
  maps: "google.com",
  spotify: "spotify.com",
  instagram: "instagram.com",
  facebook: "facebook.com",
  messenger: "facebook.com",
  netflix: "netflix.com",
  discord: "discord.com",
  telegram: "telegram.org",
  reddit: "reddit.com",
  teams: "microsoft.com",
  chatgpt: "openai.com",
  chrome: "google.com",
  gmail: "google.com",
  tiktok: "tiktok.com",
  snapchat: "snapchat.com",
  pinterest: "pinterest.com",
  uber: "uber.com",
  airbnb: "airbnb.com",
  zoom: "zoom.us",
  "booking.com": "booking.com",
  skyscanner: "skyscanner.com",
  preferences: "apple.com",
  settings: "apple.com",
  opal: "withopal.com",
  "one-sec": "one-sec.app",
  "one sec": "one-sec.app",
  freedomblocker: "freedom.to",
  freedom: "freedom.to",
  aivapp: "primevideo.com",
  "prime video": "primevideo.com",
  cbs: "paramountplus.com",
  "paramount+": "paramountplus.com",
};

function normalizeDomain(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^web:/i, "")
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .split("/")[0]!
    .split("?")[0]!;
}

function isWebsiteCategory(category?: string | null): boolean {
  return category?.trim().toLowerCase() === "website";
}

export function resolveBrandfetchDomain({
  displayName,
  appIdentifier,
  category,
}: AppIconInput): string | null {
  const trimmedId = (appIdentifier ?? "").trim();
  const trimmedDisplay = (displayName ?? "").trim();
  const idKey = trimmedId.toLowerCase();

  if (isWebsiteCategory(category)) {
    const domainSource = trimmedDisplay || trimmedId.replace(/^web:/i, "");
    const domain = normalizeDomain(domainSource);
    return domain || null;
  }

  if (idKey && idKey in IDENTIFIER_BRANDFETCH_DOMAINS) {
    return IDENTIFIER_BRANDFETCH_DOMAINS[idKey]!;
  }

  const displayKey = trimmedDisplay.toLowerCase();
  if (displayKey && displayKey in DISPLAY_NAME_BRANDFETCH_DOMAINS) {
    return DISPLAY_NAME_BRANDFETCH_DOMAINS[displayKey]!;
  }

  return null;
}

export function brandfetchLogoIdentifierFromMapping({
  brandfetchBrandId,
  brandfetchDomain,
}: {
  brandfetchBrandId?: string | null;
  brandfetchDomain?: string | null;
}): BrandfetchLogoIdentifier | null {
  const brandId = brandfetchBrandId?.trim();
  if (brandId) return { type: "brandId", value: brandId };

  const domain = brandfetchDomain?.trim();
  if (domain) return { type: "domain", value: domain };

  return null;
}

export function brandfetchIconUrl(
  identifier: string | BrandfetchLogoIdentifier,
  size: number,
  clientId: string,
): string {
  const retina = size * 2;
  const logoIdentifier = typeof identifier === "string"
    ? { type: "domain" as const, value: identifier }
    : identifier;
  const route = logoIdentifier.type === "domain"
    ? `domain/${encodeURIComponent(logoIdentifier.value)}`
    : encodeURIComponent(logoIdentifier.value);
  return `https://cdn.brandfetch.io/${route}/h/${retina}/w/${retina}/theme/dark/fallback/lettermark/icon.png?c=${encodeURIComponent(clientId)}`;
}

export function resolveInitialIconSource({
  slug,
  domain,
  isOnline,
  clientId,
}: {
  slug: string | null;
  domain: string | null;
  isOnline: boolean;
  clientId: string;
}): IconSource {
  if (slug) return "local";
  if (isOnline && domain && clientId) return "brandfetch";
  return "dot";
}

export function resolveIconSourceAfterLocalError({
  domain,
  isOnline,
  clientId,
}: {
  domain: string | null;
  isOnline: boolean;
  clientId: string;
}): IconSource {
  if (isOnline && domain && clientId) return "brandfetch";
  return "dot";
}
